<template>
  <div class="ProgramSeason">
    <h3 :id="headingId">
      <div class="button">
        <TextDelta
          class="name"
          v-text="name"
        />
      </div>
    </h3>
    <section class="panel">
      <div class="ProgramSeason__inner">
        <template v-if="display === 'options'">
          <div class="ProgramSeason__details">
            <div
              v-for="(column, index) in columns"
              :key="'column-' + index"
              :class="'ProgramSeason__detail_' + columns.length"
              class="ProgramSeason__detail"
            >
              <ProgramDetail
                v-for="(content, index) in column.content"
                :key="'content-' + index"
                :name="content.heading"
                class="detail ProgramSeason__detail-heading"
              >
                <TextDelta
                  class="ProgramSeason__detail-desc"
                  v-html="content.description"
                />
              </ProgramDetail>
            </div>
          </div>
          <p
            v-if="description"
            class="ProgramSeason__description ProgramSeason__description_options"
            v-text="description"
          />
          <ul class="ProgramSeason__options">
            <li
              v-for="(option, index) in options"
              :key="'option-' + index"
              class="ProgramSeason__option"
            >
              <TextEpsilon
                class="ProgramSeason__option-season"
                v-text="name"
              />
              <BaseImage
                v-if="option.badge"
                :id="option.badge"
                class="ProgramSeason__option-badge"
              />
              <TextDelta
                class="ProgramSeason__option-program"
                v-html="option.title || $parent.$parent.$parent.$parent.name"
              />
              <TextDelta
                class="ProgramSeason__option-label"
                v-text="option.label"
              />
              <TextDelta
                class="ProgramSeason__option-price"
                v-text="'$' + option.price"
              />
              <LinkCta
                :href="option.link || mailto"
                class="link ProgramSeason__option-cta"
              >Sign Up Now</LinkCta>
            </li>
          </ul>
        </template>
        <template v-else>
          <div class="ProgramSeason__left">
            <ProgramDetail
              v-if="daysTimes"
              name="Days & Times"
              class="detail"
            >
              <TextDelta v-html="daysTimes"/>
            </ProgramDetail>
            <template v-else>
              <ProgramDetail
                v-if="days"
                name="Days"
                class="detail"
              >
                <TextDelta v-text="days"/>
              </ProgramDetail>
              <ProgramDetail
                v-if="time"
                name="Time"
                class="detail"
              >
                <TextDelta v-text="time"/>
              </ProgramDetail>
            </template>
            <ProgramDetail
              v-if="pricing"
              name="Pricing"
              class="detail ProgramSeason__detail_pricing"
            >
              <div class="ProgramSeason__detail-value_pricing">
                <TextDelta v-text="`$${pricing.price}`"/>
                <DisclosureButton
                  v-if="pricing.discounts"
                  :controls="discountsId"
                  class="ProgramSeason__discounts-button"
                  @click.native="discountsExpanded = !discountsExpanded"
                >
                  <TextEpsilon>See Discounts</TextEpsilon>
                  <!-- eslint-disable max-len -->
                  <DisclosureIndicator
                    :expanded="discountsExpanded"
                    class="ProgramSeason__discounts-indicator ProgramSeason__discounts-indicator_button"
                  />
                  <!-- <div>
                    <BaseArrow
                      :direction="discountsExpanded ? 'up' : 'down'"
                      class="ProgramSeason__discounts-indicator ProgramSeason__discounts-indicator_arrow"
                    />
                  </div> -->
                  <!-- eslint-enable max-len -->
                </DisclosureButton>
                <DisclosureContainer
                  :id="discountsId"
                  tag="dl"
                  class="ProgramSeason__discounts-tooltip"
                >
                  <template
                    v-for="({ label, price }) in pricing.discounts"
                  >
                    <dt
                      is="TextEpsilon"
                      :key="label"
                      class="ProgramSeason__discounts-label"
                      v-text="label"
                    />
                    <dd
                      is="TextDelta"
                      :key="price"
                      class="ProgramSeason__discounts-price"
                      v-text="`$${price}`"
                    />
                  </template>
                </DisclosureContainer>
              </div>
            </ProgramDetail>
          </div>
          <p
            v-if="description"
            class="ProgramSeason__description ProgramSeason__right"
            v-text="description"
          />
          <ProgramDetail
            v-else
            name="Sessions"
            class="detail ProgramSeason__sessions ProgramSeason__right"
          >
            <div
              v-if="months"
              class="ProgramSeason__months-small"
            >
              <ul class="ProgramSeason__months">
                <li
                  v-for="month in months"
                  :key="month.name"
                  class="ProgramSeason__month"
                >
                  <button
                    :class="{ 'ProgramSeason__month-button_active': currentMonth === month }"
                    class="ProgramSeason__month-button"
                    @click="currentMonth = month"
                  >
                    <TextZeta v-text="month.name"/>
                  </button>
                </li>
              </ul>
              <ViewProgramDetailsSeasonsSeasonSessions
                :key="currentMonth.name"
                :sessions="currentMonth.sessions"
                class="month__sessions"
              />
            </div>
            <ul
              v-if="months"
              class="ProgramSeason__months-large"
            >
              <li
                v-for="({ name, sessions }) in months"
                :key="name"
                class="month"
              >
                <TextZeta
                  class="month__name"
                  v-text="name"
                />
                <ViewProgramDetailsSeasonsSeasonSessions
                  :sessions="sessions"
                  class="month__sessions"
                />
              </li>
            </ul>
            <ViewProgramDetailsSeasonsSeasonSessions
              v-if="sessions"
              :sessions="sessions"
            />
          </ProgramDetail>
          <div class="cta-wrapper">
            <LinkCta
              :href="mailto"
              class="link"
            >Sign Up Now</LinkCta>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
  import LinkCta from './LinkCta';
  import TextZeta from './TextZeta';
  import BaseImage from './BaseImage';
  import TextDelta from './TextDelta';
  import TextEpsilon from './TextEpsilon';
  import ProgramDetail from './ProgramDetail';
  import TransitionSlide from './TransitionSlide';
  import DisclosureButton from './DisclosureButton';
  import DisclosureContainer from './DisclosureContainer';
  import DisclosureIndicator from './DisclosureIndicator';
  import ViewProgramDetailsSeasonsSeasonSessions from './ViewProgramDetailsSeasonsSeasonSessions';

  export default {
    components: {
      LinkCta,
      TextZeta,
      BaseImage,
      TextDelta,
      TextEpsilon,
      ProgramDetail,
      TransitionSlide,
      DisclosureButton,
      DisclosureContainer,
      DisclosureIndicator,
      ViewProgramDetailsSeasonsSeasonSessions,
    },
    props: {
      name: {
        type: String,
        required: true,
      },
      days: {
        type: String,
        default: null,
      },
      time: {
        type: String,
        default: null,
      },
      daysTimes: {
        type: String,
        default: null,
      },
      description: {
        type: String,
        default: null,
      },
      months: {
        type: Array,
        default: null,
      },
      sessions: {
        type: Array,
        default: null,
      },
      pricing: {
        type: Object,
        default: null,
      },
      length: {
        type: Number,
        required: true,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      initiallyExpanded: {
        type: Boolean,
        default: false,
      },
      mailto: {
        type: String,
        default: null,
      },
      display: {
        type: String,
        required: true,
      },
      columns: {
        type: Array,
        default: null,
      },
      options: {
        type: Array,
        default: null,
      },
    },
    data() {
      return {
        currentMonth: this.months ? this.months[0] : null,
        expanded: this.initiallyExpanded,
        discountsExpanded: false,
      };
    },
    computed: {
      headingId() {
        return `heading_${this._uid}`;
      },
      panelId() {
        return `panel_${this._uid}`;
      },
      discountsId() {
        return `discounts_${this._uid}`;
      },
    },
    methods: {
      toggle() {
        if (this.disabled) {
          return;
        }

        this.expanded = !this.expanded;

        this.$emit(this.expanded ? 'expand' : 'collapse');
      },
    },
  };
</script>

<style
  scoped
  lang="scss"
>
  .ProgramSeason__description {
    margin-top: spacing(base-gamma);
    line-height: line-height(copy);

    @include breakpoint(tablet) {
      margin-top: 0;
    }
  }

  .ProgramSeason__detail-value_pricing {
    position: relative;
    display: flex;
    justify-content: space-between;
  }

  .ProgramSeason__discounts-button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: color(bronze);
    padding: spacing(base-beta);
    margin-top: -1em;

    &[aria-expanded="true"] {
      background-color: color(slate);
    }

    @include breakpoint(tablet) {
      margin-top: -0.75em;
    }
  }

  .ProgramSeason__discounts-indicator {
    margin-left: spacing(base-alpha);
  }

  .ProgramSeason__discounts-label {
    color: color(bronze);

    .ProgramSeason__discounts-price + & {
      margin-top: spacing(base-delta);
    }
  }

  .ProgramSeason__discounts-price {
    color: color(white);
    margin-top: spacing(base-beta);
  }

  .ProgramSeason__discounts-tooltip {
    padding: spacing(base-gamma);
    background-color: color(slate);
    position: absolute;
    top: 100%;
    right: 0;
  }

  .ProgramSeason__months {
    display: flex;
  }

  .ProgramSeason__month {
    & + & {
      margin-left: spacing(base-beta);
    }
  }

  .ProgramSeason__month-button {
    color: color(bronze);
    outline-style: none;
  }

  .ProgramSeason__month-button_active {
    color: color(red);
    border-bottom: border(bronze);
  }

  .ProgramSeason__months-small {
    @include breakpoint(tablet) {
      display: none;
    }
  }

  .ProgramSeason__months-large {
    display: none;

    @include breakpoint(tablet) {
      display: flex;
    }
  }

  .ProgramSeason__sessions {
    margin-top: spacing(base-delta);

    @include breakpoint(tablet) {
      margin-top: 0;
    }
  }

  .button {
    $padding: spacing(base-delta);

    display: flex;
    padding-top: $padding;
    padding-bottom: $padding;
    width: 100%;
    justify-content: space-between;
  }

  .detail {
    & + & {
      margin-top: spacing(base-delta);
    }
  }

  .ProgramSeason__left {
    @include breakpoint(tablet) {
      padding-right: spacing(base-gamma) / 2;
      width: 40%;
    }

    @include breakpoint(tablet) {
      width: 30%;
    }
  }

  .ProgramSeason__right {
    @include breakpoint(tablet) {
      padding-left: spacing(base-gamma) / 2;
      width: 60%;
    }

    @include breakpoint(tablet) {
      width: 70%;
    }
  }

  .month {
    $padding: spacing(base-beta);

    @include column;

    flex-grow: 1;
    border-left: border(bronze);
  }

  .month__name {
    color: color(red);
  }

  .month__sessions {
    margin-top: spacing(base-beta);
  }

  .name {
    color: color(red);

    // [aria-expanded="true"] & {
    //   color: color(red);
    // }
  }

  .ProgramSeason__inner {
    padding-top: spacing(base-delta);
    padding-bottom: spacing(base-delta);

    // @include breakpoint(phone) {
    //   padding-right: spacing(base-gamma);
    //   padding-left: spacing(base-gamma);
    // }

    @include breakpoint(tablet) {
      display: flex;
      flex-wrap: wrap;
    }
  }

  .ProgramSeason {
    padding-top: spacing(base-delta);
    padding-bottom: spacing(base-delta);
    border-top: border(bronze);

    // &:last-child {
    //   border-bottom: border(bronze);
    // }
  }

  .cta-wrapper {
    margin-top: spacing(base-delta);
    text-align: center;

    @include breakpoint(tablet) {
      margin-left: auto;
    }
  }

  .ProgramSeason__heading-indicator {
    margin-right: spacing(base-beta);

    .season:only-child & {
      display: none;
    }
  }

  .ProgramSeason__details {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
  }

  .ProgramSeason__detail {
    width: 100%;
    padding-right: spacing(base-beta);
    padding-left: spacing(base-beta);
    border-left: 2px solid color(light-grey);
    text-transform: uppercase;
    font-size: 1.6rem;

    & + & {
      padding-top: spacing(base-delta);
    }
  }

  @include breakpoint(phone) {
    .ProgramSeason__detail {
      width: 50%;

      & + &:nth-child(2) {
        padding-top: 0;
      }
    }

    .ProgramSeason__detail_1 {
      width: 100%;
    }
  }

  @include breakpoint(tablet) {
    .ProgramSeason__detail {
      & + & {
        padding-top: 0;
      }
    }

    .ProgramSeason__detail_1 {
      width: 100%;
    }

    .ProgramSeason__detail_2 {
      width: 50%;
    }

    .ProgramSeason__detail_3 {
      width: calc(100% / 3);
    }

    .ProgramSeason__detail_4 {
      width: 25%;
    }
  }

  .ProgramSeason__options {
    width: 100%;
    margin-top: 2 * spacing(base-gamma);
    justify-content: center;

    @include breakpoint(tablet) {
      display: flex;
      width: calc(100% + #{2 * spacing(base-gamma)});
      margin-right: -1 * spacing(base-gamma);
      margin-left: -1 * spacing(base-gamma);
    }
  }

  .ProgramSeason__option {
    position: relative;
    display: flex;
    padding: spacing(base-delta) spacing(base-delta) spacing(base-delta) * 2;
    margin-top: spacing(base-gamma);
    flex-direction: column;
    align-items: center;
    background-color: color(white);

    @include breakpoint(tablet) {
      width: calc(100% / 3);
      margin-top: 0;
      margin-right: spacing(base-gamma);
      margin-left: spacing(base-gamma);
    }
  }

  .ProgramSeason__option-season {
    align-self: flex-start;
    color: color(light-grey);
    margin-top: -5px;
    margin-bottom: spacing(base-delta) - spacing(base-gamma);
    text-transform: none;
  }

  .ProgramSeason__option-label {
    color: color(red);
    // text-transform: uppercase;
    font-size: 1.6rem;
    margin-top: spacing(base-gamma);
  }

  .ProgramSeason__option-price {
    margin-top: spacing(base-delta);
    margin-bottom: spacing(base-delta);
    font-size: 2.4rem;
  }

  .ProgramSeason__option-cta {
    // margin-top: spacing(base-gamma);
    margin-top: auto;
  }

  .ProgramSeason__description_options {
    margin-top: 2 * spacing(base-gamma);

    .ProgramSeason__details:empty + & {
      margin-top: 0;
    }
  }

  .ProgramSeason__option-program {
    font-size: 2rem;
    // text-transform: uppercase;
    margin-top: spacing(base-gamma);
    text-align: center;
  }

  .ProgramSeason__option-badge {
    height: 60px;
    margin-top: spacing(base-gamma);
  }

  .ProgramSeason__detail-desc {
    line-height: line-height(copy);
    text-transform: none;

  }
</style>
